import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GroundworksAuthLifecycleService, MsalService } from '@groundworks/ngx-groundworks-auth';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { GROUP_ID_TO_ROLE, setUserRole } from '../models/roles.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly msalService: MsalService = inject(MsalService);
  private readonly authLifecycle = inject(GroundworksAuthLifecycleService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly checkMemberGroupsEndpoint = 'https://graph.microsoft.com/v1.0/me/checkMemberGroups';
  private readonly profileEndpoint = 'https://graph.microsoft.com/v1.0/me';
  private readonly graphScopes = ['User.Read'];
  private readonly profile$ = new BehaviorSubject<ProfileResponse | null>(null);

  whoami$ = this.profile$.pipe(
    filter((profile): profile is ProfileResponse => !!profile),
    map(profile => profile.displayName)
  );

  setProfile(profile: ProfileResponse): void {
    this.profile$.next(profile);
  }

  async applyRoles(accessToken: string): Promise<void> {
    const memberGroupIds = await this.checkMemberGroups(accessToken);
    for (const groupId of memberGroupIds) {
      const role = GROUP_ID_TO_ROLE[groupId];
      if (role) {
        setUserRole(role, true);
      }
    }
  }

  async getProfile(accessToken: string): Promise<ProfileResponse> {
    return await firstValueFrom(
      this.http.get<ProfileResponse>(this.profileEndpoint, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    );
  }

  async getAccountAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No active account found');
    }
    const accessTokenResult =
      await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: this.graphScopes,
      });
    return accessTokenResult.accessToken;
  }

  /**
   * Redirects to sign in for the Graph token, which the page load after the redirect then uses.
   * The library's login() resolves, instead of rejecting, when the user goes Back to a page
   * restored from the back/forward cache.
   */
  async signInAgain(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount() ?? undefined;
    await this.authLifecycle.login({ account, scopes: this.graphScopes });
  }

  async checkMemberGroups(accessToken: string): Promise<string[]> {
    const groupIds = Object.keys(GROUP_ID_TO_ROLE);

    const response = await firstValueFrom(
      this.http.post<CheckMemberGroupsResponse>(
        this.checkMemberGroupsEndpoint,
        { groupIds },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      )
    );

    return response.value;
  }
}

interface CheckMemberGroupsResponse {
  value: string[];
}

export interface ProfileResponse {
  id: string;
  displayName: string;
  // Graph returns null for unset optional properties.
  givenName: string | null;
  surname: string | null;
  userPrincipalName: string;
  mail: string | null;
  mobilePhone: string | null;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
}
